import type { LocaleTextMap } from "./types";

export interface PathContext {
  session: Record<string, unknown>;
  workflow?: Record<string, unknown> | null;
  uiAction?: Record<string, unknown> | null;
  extras?: Record<string, unknown> | null;
}

type TargetKey = "session" | "workflow" | "uiAction" | "extras";

const PATH_PREFIX_RESOLVERS: Array<{
  prefix: string;
  resolve: (path: string, context: PathContext) => unknown;
  targetKey: TargetKey;
}> = [
  {
    prefix: "@session.",
    resolve: (path, context) => resolvePath(context.session, path),
    targetKey: "session"
  },
  {
    prefix: "@workflow.",
    resolve: (path, context) => resolvePath(context.workflow ?? {}, path),
    targetKey: "workflow"
  },
  {
    prefix: "@uiAction.",
    resolve: (path, context) => resolvePath(context.uiAction ?? {}, path),
    targetKey: "uiAction"
  },
  {
    prefix: "@extras.",
    resolve: (path, context) => resolvePath(context.extras ?? {}, path),
    targetKey: "extras"
  }
];

const ROOT_BINDING_RESOLVERS: Record<string, (context: PathContext) => unknown> = {
  "@session": (context) => context.session,
  "@workflow": (context) => context.workflow ?? null,
  "@uiAction": (context) => context.uiAction ?? null,
  "@extras": (context) => context.extras ?? null
};

export function translateText(
  map: LocaleTextMap | undefined,
  language: string,
  fallbackLanguage: string
): string {
  if (!map) {
    return "";
  }

  if (map[language]) {
    return map[language];
  }

  if (map[fallbackLanguage]) {
    return map[fallbackLanguage];
  }

  const first = Object.values(map)[0];
  return typeof first === "string" ? first : "";
}

export function resolveBindingValue(binding: string | undefined, context: PathContext): unknown {
  if (!binding) {
    return undefined;
  }

  if (ROOT_BINDING_RESOLVERS[binding]) {
    return ROOT_BINDING_RESOLVERS[binding](context);
  }

  for (const { prefix, resolve } of PATH_PREFIX_RESOLVERS) {
    if (binding.startsWith(prefix)) {
      const path = binding.slice(prefix.length);
      return resolve(path, context);
    }
  }

  return resolvePath(context.session, binding);
}

export function setBindingValue(
  binding: string | undefined,
  value: unknown,
  context: PathContext
): PathContext {
  if (!binding) {
    return context;
  }

  for (const resolver of PATH_PREFIX_RESOLVERS) {
    if (binding.startsWith(resolver.prefix)) {
      const path = binding.slice(resolver.prefix.length);
      return applyPathUpdate(resolver.targetKey, path, value, context);
    }
  }

  if (binding in ROOT_BINDING_RESOLVERS) {
    if (binding === "@session" && value && typeof value === "object") {
      return {
        session: value as Record<string, unknown>,
        workflow: context.workflow,
        uiAction: context.uiAction,
        extras: context.extras
      } satisfies PathContext;
    }
    return context;
  }

  return applyPathUpdate("session", binding, value, context);
}

function applyPathUpdate(
  targetKey: TargetKey,
  path: string,
  value: unknown,
  context: PathContext
): PathContext {
  const clone: PathContext = {
    session: { ...context.session },
    workflow: context.workflow ? { ...context.workflow } : context.workflow ?? null,
    uiAction: context.uiAction ? { ...context.uiAction } : context.uiAction ?? null,
    extras: context.extras ? { ...context.extras } : context.extras ?? null
  };

  if (targetKey !== "session" && clone[targetKey] == null) {
    clone[targetKey] = {};
  }

  setPath(clone[targetKey] as Record<string, unknown>, path, value);
  return clone;
}

export function resolveTemplateValue(input: unknown, context: PathContext): unknown {
  if (typeof input === "string") {
    for (const { prefix, resolve } of PATH_PREFIX_RESOLVERS) {
      if (input.startsWith(prefix)) {
        const path = input.slice(prefix.length);
        return resolve(path, context);
      }
    }
    return input;
  }

  if (Array.isArray(input)) {
    return input.map((item) => resolveTemplateValue(item, context));
  }

  if (input && typeof input === "object") {
    return Object.fromEntries(
      Object.entries(input as Record<string, unknown>).map(([key, value]) => [
        key,
        resolveTemplateValue(value, context)
      ])
    );
  }

  return input;
}

export function resolvePath(source: unknown, path: string): unknown {
  if (!source || typeof source !== "object") {
    return undefined;
  }

  return path
    .split(".")
    .filter((segment) => segment.length > 0)
    .reduce<unknown>((current, segment) => {
      if (!current || typeof current !== "object") {
        return undefined;
      }
      return (current as Record<string, unknown>)[segment];
    }, source);
}

export function setPath(target: Record<string, unknown>, path: string, value: unknown): void {
  const segments = path
    .split(".")
    .filter((segment) => segment.length > 0);

  if (segments.length === 0) {
    return;
  }

  let current: Record<string, unknown> = target;
  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    if (isLast) {
      current[segment] = value as never;
    } else {
      const next = current[segment];
      if (!next || typeof next !== "object") {
        current[segment] = {} as never;
      }
      current = current[segment] as Record<string, unknown>;
    }
  });
}
